import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { AuthClient } from '@/contexts/auth';
import { readSelectedFishGroupId } from '@/contexts/cage-filter-context';
import { useDemoMode } from '@/contexts/demo/DemoContext';
import type { OverviewLocation } from '@/domain/client';
import { useGetClientOverview } from '@/domain/client';
import { applyDemoMaskToOverviewLocation } from '@/utils/demoMasking';
import { resolvePenCohortName } from '@/utils/resolvePenCohortName';

import {
    groupCagesByStatus,
    groupClientsByStatus,
    groupLocationsByStatus,
} from './breadcrumbGrouping';
import type { BreadcrumbCage, CagesByStatus, ClientsByStatus, LocationsByStatus } from './types';

interface BreadcrumbEntriesInput {
    client?: AuthClient;
    clients: AuthClient[];
    locationId?: number;
    cageId?: number;
    showLocationsOnClientOverview: boolean;
    showCagesOnLocationOverview: boolean;
}

export interface BreadcrumbEntries {
    isLoading: boolean;
    currentLocation: OverviewLocation | null;
    setCurrentLocation: Dispatch<SetStateAction<OverviewLocation | null>>;
    currentCage: BreadcrumbCage | null;
    /** The cohort the pen's pages are about, when there is one to name. */
    penCohortName: string | undefined;
    clientsByStatus: ClientsByStatus;
    locationsByStatus: LocationsByStatus;
    cagesByStatus: CagesByStatus;
    setClientNameFilter: Dispatch<SetStateAction<string>>;
}

/** What the client, location and pen dropdowns list, and which entry of each is current. */
export function useBreadcrumbEntries(input: BreadcrumbEntriesInput): BreadcrumbEntries {
    const {
        client,
        clients,
        locationId,
        cageId,
        showLocationsOnClientOverview,
        showCagesOnLocationOverview,
    } = input;
    const location = useLocation();
    const { isDemoMode } = useDemoMode();

    const { isLoading, data: clientOverview } = useGetClientOverview(client?.id ?? 0, {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
        enabled: client != null,
    });

    const internalPage = location.pathname.startsWith('/internal');
    const { t } = useTranslation();

    // Mask locations when demo mode is enabled and NOT on internal pages
    const locations = useMemo(() => {
        if (!clientOverview?.locations || !isDemoMode || internalPage) {
            return clientOverview?.locations;
        }
        return clientOverview.locations.map((loc) => applyDemoMaskToOverviewLocation(loc, t));
    }, [clientOverview?.locations, isDemoMode, internalPage, t]);

    const [currentLocation, setCurrentLocation] = useState<OverviewLocation | null>(null);
    const [currentCage, setCurrentCage] = useState<BreadcrumbCage | null>(null);
    const [clientNameFilter, setClientNameFilter] = useState('');
    const [clientsByStatus, setClientsByStatus] = useState<ClientsByStatus>({
        active: [],
        inactive: [],
    });
    const [locationsByStatus, setLocationsByStatus] = useState<LocationsByStatus>({
        active: [],
        inactive: [],
    });
    const [cagesByStatus, setCagesByStatus] = useState<CagesByStatus>({
        active: [],
        inactive: [],
        empty: [],
        historical: [],
    });

    // Which cohort the pen's pages are about. Taken from the URL first so a closed
    // group reached through `?fg=` is named here too, instead of the breadcrumb
    // quietly claiming the pen's current occupant while the charts show another.
    const selectedFishGroupId = readSelectedFishGroupId(location.search);
    const penCohortName = resolvePenCohortName(currentLocation, cageId, selectedFishGroupId);

    // Reset currentLocation when showLocationsOnClientOverview is true
    useEffect(() => {
        if (showLocationsOnClientOverview) {
            setCurrentLocation(null);
        }
    }, [showLocationsOnClientOverview]);

    // Reset currentCage when showCagesOnLocationOverview is true
    useEffect(() => {
        if (showCagesOnLocationOverview) {
            setCurrentCage(null);
        }
    }, [showCagesOnLocationOverview]);

    useEffect(() => {
        if (!clients) return;
        setClientsByStatus(groupClientsByStatus(clients, clientNameFilter));
    }, [clients, clientNameFilter]);

    useEffect(() => {
        if (!locations) return;
        setLocationsByStatus(groupLocationsByStatus(locations));
    }, [locations]);

    useEffect(() => {
        if (!currentLocation) return;
        setCagesByStatus(groupCagesByStatus(currentLocation));
    }, [currentLocation]);

    useEffect(() => {
        if (!locationId || !locations) return;
        const currentLocation = locations.find((loc) => loc.locationId === locationId);
        setCurrentLocation(currentLocation ? currentLocation : null);
    }, [locationId, locations]);

    useEffect(() => {
        if (!currentLocation || !cageId) return;
        const found = currentLocation.cages.find((cage) => cage.cageId === cageId);
        setCurrentCage(found ? { id: found.cageId, name: found.cageName } : null);
    }, [currentLocation, cageId]);

    return {
        isLoading,
        currentLocation,
        setCurrentLocation,
        currentCage,
        penCohortName,
        clientsByStatus,
        locationsByStatus,
        cagesByStatus,
        setClientNameFilter,
    };
}
